import React, { memo, useCallback, useReducer } from 'react';
import { View, FlatList } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  Text,
  Space,
  LoadingContainer,
  WebScrollView,
  DragableFlatList,
} from '@redlink/components/atoms';
import {
  PreviewContainer,
  PreviewLinkItem,
  useDialog,
  ContactInfoItems,
} from '@redlink/components/molecules';
import {
  EducationItem,
  ExperienceItem,
  AchievementItem,
  ItemsContainer,
  SkillItem,
  PreviewGalleryPostCard,
} from '@redlink/components/muscles';
import { JobStatus } from '@redlink/components/organisms';
import {
  PreviewResumePage,
  ExperienceEditPage,
  EducationEditPage,
  AchievementsEditPage,
  ContactInfoEditPage,
  ResumeLinksEditPage,
  ResumeProjectsEditPage,
  SkillEditPage,
  BioEditPage,
} from '@redlink/components/pages';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import {
  useQuery,
  useMutation,
  getPageProfileQuery,
  getPageResumeQuery,
  setResumeItemVisibilityQuery,
  removeResumeItemQuery,
  getPagePreviewResumeQuery,
  setPageJobStatus,
  reorderResumeItem,
} from '@redlink/services';
import { initialState, reducer, types } from './store';
import {
  freshCacheForVisibility,
  freshCacheForRemoveItem,
  freshCacheJobStatus,
  freshCacheForReoderItem,
} from './cache';

export const ResumePage = memo(() => {
  const { styles } = useStyle();
  const { dictionary } = useLocale();
  const { staticData } = useValues();
  const { showConfirmDialog } = useDialog();

  const [
    {
      currentJobStatus,
      newBioIsVisible,
      currentBio,
      newExperieceIsVisible,
      currentExperiece,
      newEducationIsVisible,
      currentEducation,
      newSkillIsVisible,
      currentSkill,
      currentAchievement,
      newAchievementIsVisible,
      currentContactInfo,
      newContactInfoVisible,
      currentProjects,
      editProjectsIsVisible,
      currentLinks,
      editLinksIsVisible,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  const {
    data: { PageProfile: { pageName } = {} },
  } = useQuery(getPageProfileQuery);

  const {
    loading: getPageResumeIsBusy,
    data: { Resume = {} },
  } = useQuery(getPageResumeQuery);

  const { refetch: refetchPreviewResume } = useQuery(
    getPagePreviewResumeQuery,
    { variables: { pageName } }
  );

  const [setResumeItemVisibility] = useMutation(setResumeItemVisibilityQuery, {
    done: () => refetchPreviewResume({ pageName }),
    update: freshCacheForVisibility(),
  });

  const [removeResumeItem] = useMutation(removeResumeItemQuery, {
    done: () => {},
    update: freshCacheForRemoveItem({ pageName }),
  });

  const [submitJobStatus, { loading: submitJobStatusLoading }] = useMutation(
    setPageJobStatus,
    {
      done: () => {},
      update: freshCacheJobStatus({ pageName }),
    }
  );

  const onChangeJobStatus = (item, index) => {
    dispatch({
      type: types.SET_CURRENT_JOB_STATUS,
      payload: item,
    });
    submitJobStatus({ value: item });
  };

  const renderSkillItem = useCallback(({ item, index, dragHandle }) => {
    return (
      <SkillItem
        {...{ item, dragHandle }}
        onEditPressed={() =>
          dispatch({
            type: types.SET_MULTI,
            payload: { currentSkill: item, newSkillIsVisible: true },
          })
        }
        onChangeAvailability={(value) => {
          setResumeItemVisibility({ id: item.id, isVisible: value });
        }}
        onDelete={() => {
          showConfirmDialog({
            message: dictionary.deleteConfirmMessage,
            onConfirm: () => removeResumeItem({ id: item.id }),
          });
        }}
        dragable
      />
    );
  }, []);

  const renderExperienceItem = useCallback(({ item, index, dragHandle }) => {
    return (
      <ExperienceItem
        {...{ item, dragHandle }}
        onEditPressed={() =>
          dispatch({
            type: types.SET_MULTI,
            payload: { currentExperiece: item, newExperieceIsVisible: true },
          })
        }
        onChangeAvailability={(value) => {
          setResumeItemVisibility({ id: item.id, isVisible: value });
        }}
        onDelete={() => {
          showConfirmDialog({
            message: dictionary.deleteConfirmMessage,
            onConfirm: () => removeResumeItem({ id: item.id }),
          });
        }}
        dragable
      />
    );
  }, []);

  const renderEducationItem = useCallback(({ item, index, dragHandle }) => {
    return (
      <EducationItem
        {...{ item, dragHandle }}
        onEditPressed={() =>
          dispatch({
            type: types.SET_MULTI,
            payload: { currentEducation: item, newEducationIsVisible: true },
          })
        }
        onChangeAvailability={(value) => {
          setResumeItemVisibility({ id: item.id, isVisible: value });
        }}
        onDelete={() => {
          showConfirmDialog({
            message: dictionary.deleteConfirmMessage,
            onConfirm: () => removeResumeItem({ id: item.id }),
          });
        }}
        dragable
      />
    );
  }, []);

  const renderFeaturedProjectsItem = useCallback(({ item, index }) => {
    return (
      <PreviewGalleryPostCard
        {...{ item }}
        style={styles.featuredProjectsStyle}
        smallTile
        themeIndependent
      />
    );
  }, []);

  const renderAchievementItem = useCallback(({ item, index, dragHandle }) => {
    return (
      <AchievementItem
        {...{ item, dragHandle }}
        onEditPressed={() =>
          dispatch({
            type: types.SET_MULTI,
            payload: {
              currentAchievement: item,
              newAchievementIsVisible: true,
            },
          })
        }
        onChangeAvailability={(value) => {
          setResumeItemVisibility({ id: item.id, isVisible: value });
        }}
        onDelete={() => {
          showConfirmDialog({
            message: dictionary.deleteConfirmMessage,
            onConfirm: () => removeResumeItem({ id: item.id }),
          });
        }}
        dragable
      />
    );
  }, []);

  const renderLinks = useCallback(
    ({ item: { logoName, title, shortLinkUrl }, index, dragHandle }) => {
      return (
        <PreviewLinkItem
          {...{ logoName, title, dragHandle }}
          externalUrl={shortLinkUrl}
          themeIndependent
        />
      );
    },
    []
  );

  const [reorderItem] = useMutation(reorderResumeItem, {
    done: () => refetchPreviewResume({ pageName }),
  });

  const onDragEnd = ({ destination, source, draggableId: id }, type) => {
    if (!destination || source.index === destination.index) {
      return;
    }

    reorderItem({ id, newOrder: destination.index + 1, type });

    freshCacheForReoderItem({
      oldIndex: source.index,
      newIndex: destination.index,
      type,
    });
  };

  const hasBio = !!Resume.contacts?.bio;

  return (
    <View style={styles.container}>
      <WebScrollView>
        <LoadingContainer
          isBusy={getPageResumeIsBusy || submitJobStatusLoading}
        >
          <ItemsContainer title={dictionary.jobStatus} noAction>
            <JobStatus
              currentJobStatus={
                currentJobStatus ? currentJobStatus : Resume.contacts?.jobStatus
              }
              onChange={onChangeJobStatus}
            />
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.talkAboutYourself}
            description={dictionary.bioDescription}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentBio: Resume.contacts?.bio,
                  newBioIsVisible: true,
                },
              })
            }
          >
            {hasBio && (
              <Text style={styles.bioText}>{Resume.contacts?.bio}</Text>
            )}
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.skills}
            description={dictionary.skillsDescription}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentSkill: null,
                  newSkillIsVisible: true,
                },
              })
            }
          >
            <DragableFlatList
              data={Resume.skills}
              ItemSeparatorComponent={() => <Space size={8} />}
              renderItem={renderSkillItem}
              onDragEnd={(value) =>
                onDragEnd(value, staticData.resumeItemType.skill)
              }
            />
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.workHistory}
            description={dictionary.workHistoryDescription}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentExperiece: null,
                  newExperieceIsVisible: true,
                },
              })
            }
          >
            <DragableFlatList
              data={Resume.experiences}
              ItemSeparatorComponent={() => <Space size={8} />}
              renderItem={renderExperienceItem}
              onDragEnd={(value) =>
                onDragEnd(value, staticData.resumeItemType.experience)
              }
            />
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.educationalHistory}
            description={dictionary.educationalHistoryDescription}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentEducation: null,
                  newEducationIsVisible: true,
                },
              })
            }
          >
            <DragableFlatList
              data={Resume.educations}
              ItemSeparatorComponent={() => <Space size={8} />}
              renderItem={renderEducationItem}
              onDragEnd={(value) =>
                onDragEnd(value, staticData.resumeItemType.education)
              }
            />
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.featuredProjects}
            description={dictionary.featuredProjectsDescription}
            style={styles.featuredProjectsContainer}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  editProjectsIsVisible: true,
                  currentProjects: Resume.projects,
                },
              })
            }
          >
            <FlatList
              horizontal
              data={Resume.projects}
              keyExtractor={({ id }) => `${id}`}
              showsVerticalScrollIndicator={false}
              renderItem={renderFeaturedProjectsItem}
              ItemSeparatorComponent={() => <Space size={8} />}
              contentContainerStyle={styles.projects}
            />
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.achievements}
            description={dictionary.achievementsDescription}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentAchievement: null,
                  newAchievementIsVisible: true,
                },
              })
            }
          >
            <DragableFlatList
              data={Resume.achievements}
              ItemSeparatorComponent={() => <Space size={8} />}
              renderItem={renderAchievementItem}
              onDragEnd={(value) =>
                onDragEnd(value, staticData.resumeItemType.achievement)
              }
            />
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.contactInfo}
            description={dictionary.contactInfoDescrption}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  currentContactInfo: Resume.contacts,
                  newContactInfoVisible: true,
                },
              })
            }
          >
            <ContactInfoItems data={Resume.contacts} panel />
          </ItemsContainer>
          <Space />
          <ItemsContainer
            title={dictionary.socialMedia}
            style={styles.socialMediaContainer}
            description={dictionary.socialMediaDescription}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  editLinksIsVisible: true,
                  currentLinks: Resume.links,
                },
              })
            }
          >
            <FlatList
              data={Resume.links}
              ItemSeparatorComponent={() => <Space size={8} />}
              renderItem={renderLinks}
            />
          </ItemsContainer>
          <Space />
        </LoadingContainer>
      </WebScrollView>

      <PreviewContainer>
        <PreviewResumePage {...{ pageName }} />
      </PreviewContainer>

      <BioEditPage
        {...{ pageName }}
        isVisible={newBioIsVisible}
        {...(currentBio && {
          data: currentBio,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_NEW_BIO_IS_VISIBLE,
            payload: false,
          });
        }}
      />

      <SkillEditPage
        {...{ pageName }}
        isVisible={newSkillIsVisible}
        {...(currentSkill && {
          data: currentSkill,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_NEW_SKILL_IS_VISIBLE,
            payload: false,
          });
        }}
      />
      <ExperienceEditPage
        {...{ pageName }}
        isVisible={newExperieceIsVisible}
        {...(currentExperiece && {
          data: currentExperiece,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_NEW_EXPERIENCE_IS_VISIBLE,
            payload: false,
          });
        }}
      />
      <EducationEditPage
        {...{ pageName }}
        isVisible={newEducationIsVisible}
        {...(currentEducation && {
          data: currentEducation,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_NEW_EDUCATION_IS_VISIBLE,
            payload: false,
          });
        }}
      />
      <AchievementsEditPage
        {...{ pageName }}
        isVisible={newAchievementIsVisible}
        {...(currentAchievement && {
          data: currentAchievement,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_NEW_ACHIEVEMENT_IS_VISIBLE,
            payload: false,
          });
        }}
      />
      <ResumeProjectsEditPage
        {...{ pageName }}
        isVisible={editProjectsIsVisible}
        {...(currentProjects && {
          currentProjects,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_EDIT_PROJECTS_IS_VISIBLE,
            payload: false,
          });
        }}
      />
      <ContactInfoEditPage
        {...{ pageName }}
        isVisible={newContactInfoVisible}
        {...(currentContactInfo && {
          data: currentContactInfo,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_NEW_CONTACT_INFO_IS_VISIBLE,
            payload: false,
          });
        }}
      />
      <ResumeLinksEditPage
        {...{ pageName }}
        isVisible={editLinksIsVisible}
        {...(currentLinks && {
          currentLinks,
        })}
        onDismiss={() => {
          dispatch({
            type: types.SET_EDIT_LINKS_IS_VISIBLE,
            payload: false,
          });
        }}
      />
    </View>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  featuredProjectsStyle: {
    width: 192,
    height: 256,
  },
  bioText: {
    fontSize: 15,
    lineHeight: 24,
    color: colors.gray23,
  },
  featuredProjectsContainer: {
    paddingHorizontal: 0,
  },
  projects: {
    paddingHorizontal: 16,
  },
}));
